import React, { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, AlertTriangle } from 'lucide-react';
import { completeEmailLinkSignIn } from '../firebase';

/**
 * Mounted on /auth/email-link-callback (see App.js). The user lands here after
 * clicking the magic link in their inbox.
 *
 * IMPORTANT: sign-in is NOT auto-run on mount. It requires an explicit button
 * click. This defends against link-scanning bots — Microsoft Defender
 * SafeLinks, Outlook/Exchange, Apple Mail Privacy Protection, etc. pre-fetch
 * URLs in emails from a headless server farm to scan for malware. A magic-link
 * is single-use: Firebase invalidates the oobCode the moment it's consumed.
 * If a scanner consumes it first, it can mint the Auth user (with empty
 * localStorage and no human to finish setup) and then the real user's click
 * fails with auth/invalid-action-code — leaving an Auth user with no
 * users/{uid} Firestore doc. Gating behind a click that bots won't perform
 * keeps the code intact for the human.
 *
 * On click we finish the sign-in round-trip, which:
 *   - Signs the user in via Firebase Auth.
 *   - Creates users/{uid} + personal orgs/{uid} on first sign-in (idempotent;
 *     same helper as Google sign-in — see firebase.js ensureUserDocAndPersonalOrg).
 *   - Rehydrates a pending Google credential and calls linkWithCredential if
 *     the user originally hit auth/account-exists-with-different-credential
 *     during Google sign-in (the Google → magic-link link-up flow).
 *
 * Then redirects to /dashboard. The downstream ProtectedRoute → OnboardingGate
 * chain handles email-verification + profile-completion gating.
 */
export default function EmailLinkCallback() {
  const navigate = useNavigate();
  // 'awaiting' → show the confirm button (initial; bot-safe — no sign-in yet).
  // 'processing' → click handler is running completeEmailLinkSignIn.
  // 'success' → signed in, redirecting.
  // 'error' → something failed; offer a fresh link.
  const [status, setStatus] = useState('awaiting');
  const [errorCode, setErrorCode] = useState('');
  const ranRef = useRef(false);

  const handleConfirm = async () => {
    // completeEmailLinkSignIn() is not idempotent (the sign-in link is
    // single-use — Firebase invalidates it server-side once consumed), so
    // guard against a double click / rapid re-entry.
    if (ranRef.current) return;
    ranRef.current = true;
    setStatus('processing');
    try {
      await completeEmailLinkSignIn();
      setStatus('success');
      navigate('/dashboard', { replace: true });
    } catch (err) {
      console.error('Email-link callback failed:', err?.code, err?.message, err);
      setStatus('error');
      setErrorCode(err?.code || err?.message || 'unknown');
      // Allow another attempt (e.g. user re-confirms the email in the
      // different-browser prompt). The oobCode is only spent on a successful
      // signInWithEmailLink, so a failed attempt leaves it reusable.
      ranRef.current = false;
    }
  };

  if (status === 'awaiting') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border border-slate-200 p-8 text-center">
          <div className="w-14 h-14 bg-[#043132]/10 text-[#043132] rounded-full flex items-center justify-center mx-auto mb-4">
            <Mail size={28} />
          </div>
          <h1 className="text-xl font-bold text-slate-800 mb-2">Confirm your sign-in</h1>
          <p className="text-sm text-slate-600 mb-6">
            Click the button below to finish signing in to EcoIntel.
          </p>
          <button
            type="button"
            onClick={handleConfirm}
            className="inline-block w-full py-3 px-4 bg-[#043132] hover:bg-[#065354] text-white font-semibold rounded-xl transition-colors"
          >
            Confirm sign-in
          </button>
        </div>
      </div>
    );
  }

  if (status === 'processing' || status === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border border-slate-200 p-8 text-center">
          <div className="w-14 h-14 bg-[#043132]/10 text-[#043132] rounded-full flex items-center justify-center mx-auto mb-4">
            <Mail size={28} />
          </div>
          <h1 className="text-xl font-bold text-slate-800 mb-2">Signing you in…</h1>
          <p className="text-sm text-slate-600">
            Just a moment while we confirm your email link.
          </p>
        </div>
      </div>
    );
  }

  // status === 'error'
  const expired = errorCode === 'auth/invalid-action-code' || errorCode === 'auth/expired-action-code';
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border border-slate-200 p-8 text-center">
        <div className="w-14 h-14 bg-red-50 text-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
          <AlertTriangle size={28} />
        </div>
        <h1 className="text-xl font-bold text-slate-800 mb-2">
          {expired ? 'Sign-in link expired' : 'We couldn’t sign you in'}
        </h1>
        <p className="text-sm text-slate-600 mb-6">
          {expired
            ? 'This sign-in link is no longer valid. Magic links expire after about an hour and can only be used once. Request a new link to continue.'
            : 'Something went wrong while completing your sign-in. Please request a new sign-in link and try again.'}
        </p>
        {errorCode && (
          <p className="text-xs text-slate-400 mb-6 font-mono break-all">
            {errorCode}
          </p>
        )}
        <Link
          to="/login"
          className="inline-block w-full py-3 px-4 bg-[#043132] hover:bg-[#065354] text-white font-semibold rounded-xl transition-colors"
        >
          Request a new sign-in link
        </Link>
      </div>
    </div>
  );
}
